import React, { useCallback, useEffect, useRef, useState } from "react";
import { Avatar, Box, CircularProgress, IconButton, InputAdornment, Stack, TextField, Tooltip, Typography } from "@mui/material";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";

import { approvalApi } from "./api/approvalApi";
import { notify } from "../../util/toast";
import { confirm as confirmDialog } from "../../util/confirmDialog";

/**
 * 결재 댓글.
 *
 * <h3>결재선의 «사유» 와 무엇이 다른가</h3>
 * 결재선 사유는 승인·반려·보류라는 <b>행위에 붙는 이력</b>이다. 결재자만, 자기 차례에, 한 번.
 * 댓글은 그것과 별개로 <b>문서를 볼 수 있는 사람이면 누구나, 언제든, 몇 번이든</b> 남긴다.
 * 재무 담당자가 "법인카드 영수증 첨부 부탁드립니다" 라고 적는 자리이고, 실제로 양식 안내문구가
 * "사용카드 번호가 변경된 경우에도 댓글로 메모 남겨주세요" 라고 이 사용을 명시하고 있다.
 *
 * 그래서 화면에서도 둘을 섞지 않는다 — 사유는 결재선 영역에, 댓글은 여기에.
 *
 * 서버 권한: 읽기·쓰기 = 볼 수 있는 사람(공람 포함), 수정·삭제 = 작성자 본인 또는 관리자.
 * 화면은 서버가 내려준 {@code mine} 만 보고 버튼을 그린다(권한 판단을 화면이 흉내내지 않는다).
 */
export default function ApprovalComments({ approvalId }) {
    const [items, setItems]     = useState([]);
    const [loading, setLoading] = useState(true);
    const [draft, setDraft]     = useState("");
    const [busy, setBusy]       = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editText, setEditText]   = useState("");
    const [pendingFiles, setPendingFiles] = useState([]);   // 아직 안 올린 첨부
    const inputRef = useRef(null);
    const fileRef  = useRef(null);

    const load = useCallback(async () => {
        if (!approvalId) return;
        try {
            const res = await approvalApi.comments(approvalId);
            setItems(Array.isArray(res) ? res : (res?.data ?? []));
        } catch {
            notify("댓글을 불러오지 못했습니다.", "error");
        } finally {
            setLoading(false);
        }
    }, [approvalId]);

    useEffect(() => { load(); }, [load]);

    const submit = async () => {
        const body = draft.trim();
        if ((!body && pendingFiles.length === 0) || busy) return;
        setBusy(true);
        try {
            // 파일만 올리는 경우도 있다 — 그때 내용은 파일명으로 대신한다(빈 댓글은 서버가 막는다).
            const res = await approvalApi.addComment(
                approvalId, body || pendingFiles.map((f) => f.name).join(", "));
            const created = res?.data ?? res;
            for (const f of pendingFiles) {
                await approvalApi.uploadCommentAttachment(created.id, f);
            }
            setDraft("");
            setPendingFiles([]);
            await load();
        } catch {
            notify("댓글을 남기지 못했습니다.", "error");
        } finally {
            setBusy(false);
            inputRef.current?.focus();
        }
    };

    /*
     * 수정·삭제도 등록과 같은 busy 잠금을 쓴다.
     *
     * 요청이 끝나기 전에 한 번 더 눌리면 두 번째가 «없는 댓글» 에 도착해 실패 토스트만 뜬다 —
     * 첫 요청은 성공했는데 화면에는 빨간 메시지만 남는, 결재 승인에서 겪은 것과 같은 모양이다.
     * 플래그를 따로 두지 않는 건 셋 다 «댓글 목록이 바뀌는 중» 이라는 한 가지 상태이기 때문이다.
     */
    const saveEdit = async (id) => {
        const body = editText.trim();
        if (!body || busy) return;
        setBusy(true);
        try {
            await approvalApi.editComment(id, body);
            setEditingId(null);
            await load();
        } catch {
            notify("댓글을 수정하지 못했습니다.", "error");
        } finally {
            setBusy(false);
        }
    };

    const remove = async (id) => {
        if (busy) return;
        if (!(await confirmDialog({ message: "댓글을 삭제하시겠습니까?" }))) return;
        setBusy(true);
        try {
            await approvalApi.deleteComment(id);
            await load();
        } catch {
            notify("댓글을 삭제하지 못했습니다.", "error");
        } finally {
            setBusy(false);
        }
    };

    // Enter 로 보내고 Shift+Enter 로 줄바꿈 — 짧은 메모가 대부분이라 이 쪽이 손이 덜 간다.
    const onKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            submit();
        }
    };

    return (
        <Box>
            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                    <CircularProgress size={16} />
                </Box>
            ) : items.length === 0 ? (
                <Typography sx={{ fontSize: 12.5, color: "#94a3b8", mb: 1.5 }}>
                    아직 댓글이 없습니다.
                </Typography>
            ) : (
                <Stack spacing={1.25} sx={{ mb: 1.5 }}>
                    {items.map((c) => (
                        <Box key={c.id} sx={{ display: "flex", gap: 1 }}>
                            <Avatar sx={{ width: 26, height: 26, fontSize: 11, bgcolor: "#64748b", mt: 0.2 }}>
                                {(c.authorName || "?").charAt(0)}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Stack direction="row" alignItems="center" spacing={0.75}>
                                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#0f172a" }}>
                                        {c.authorName}
                                    </Typography>
                                    {(c.authorPosition || c.authorDept) && (
                                        <Typography sx={{ fontSize: 11, color: "#94a3b8" }} noWrap>
                                            {[c.authorPosition, c.authorDept].filter(Boolean).join(" · ")}
                                        </Typography>
                                    )}
                                    <Box sx={{ flex: 1 }} />
                                    <Typography sx={{ fontSize: 11, color: "#cbd5e1", flexShrink: 0 }}>
                                        {formatWhen(c.createdAt)}{c.updatedAt ? " (수정됨)" : ""}
                                    </Typography>
                                    {c.mine && editingId !== c.id && (
                                        <>
                                            <Tooltip title="수정">
                                                <IconButton size="small" sx={{ p: 0.2 }}
                                                    onClick={() => { setEditingId(c.id); setEditText(c.content); }}>
                                                    <EditRoundedIcon sx={{ fontSize: 13, color: "#cbd5e1" }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="삭제">
                                                <IconButton size="small" sx={{ p: 0.2 }} disabled={busy} onClick={() => remove(c.id)}>
                                                    <DeleteOutlineRoundedIcon sx={{ fontSize: 14, color: "#cbd5e1" }} />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    )}
                                </Stack>

                                {editingId === c.id ? (
                                    <Stack direction="row" spacing={0.5} alignItems="flex-start" sx={{ mt: 0.4 }}>
                                        <TextField
                                            size="small" fullWidth multiline maxRows={6} value={editText}
                                            onChange={(e) => setEditText(e.target.value)}
                                            inputProps={{ style: { fontSize: 12.5 } }}
                                        />
                                        <IconButton size="small" disabled={busy} onClick={() => saveEdit(c.id)} sx={{ p: 0.4 }}>
                                            <SendRoundedIcon sx={{ fontSize: 15, color: "#2563eb" }} />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => setEditingId(null)} sx={{ p: 0.4 }}>
                                            <CloseRoundedIcon sx={{ fontSize: 15, color: "#94a3b8" }} />
                                        </IconButton>
                                    </Stack>
                                ) : (
                                    <>
                                        <Typography sx={{
                                            fontSize: 12.5, color: "#334155", mt: 0.2,
                                            whiteSpace: "pre-wrap", wordBreak: "break-word",
                                        }}>
                                            {c.content}
                                        </Typography>
                                        {(c.attachments || []).length > 0 && (
                                            <Stack direction="row" spacing={0.75} flexWrap="wrap" rowGap={0.5} sx={{ mt: 0.6 }}>
                                                {c.attachments.map((att) => (
                                                    <Box key={att.id} component="a"
                                                        href={att.fileUrl} target="_blank" rel="noreferrer"
                                                        sx={{
                                                            display: "flex", alignItems: "center", gap: 0.5,
                                                            px: 1, py: 0.4, borderRadius: 1, textDecoration: "none",
                                                            border: "1px solid #e2e8f0", bgcolor: "#f8fafc",
                                                            "&:hover": { bgcolor: "#f1f5f9" },
                                                        }}>
                                                        <AttachFileRoundedIcon sx={{ fontSize: 13, color: "#94a3b8" }} />
                                                        <Typography sx={{ fontSize: 11.5, color: "#475569", maxWidth: 220 }} noWrap>
                                                            {att.originalFileName}
                                                        </Typography>
                                                        <DownloadRoundedIcon sx={{ fontSize: 13, color: "#cbd5e1" }} />
                                                    </Box>
                                                ))}
                                            </Stack>
                                        )}
                                    </>
                                )}
                            </Box>
                        </Box>
                    ))}
                </Stack>
            )}

            {/* 첨부 대기 목록 — 아직 올리지 않은 파일. 댓글을 보내는 순간 함께 올라간다. */}
            {pendingFiles.length > 0 && (
                <Stack direction="row" spacing={0.75} flexWrap="wrap" rowGap={0.75} sx={{ mb: 0.75 }}>
                    {pendingFiles.map((f, i) => (
                        <Box key={`${f.name}-${i}`} sx={{
                            display: "flex", alignItems: "center", gap: 0.5,
                            px: 1, py: 0.4, borderRadius: 1,
                            border: "1px solid #e2e8f0", bgcolor: "#f8fafc",
                        }}>
                            <AttachFileRoundedIcon sx={{ fontSize: 13, color: "#94a3b8" }} />
                            <Typography sx={{ fontSize: 11.5, color: "#475569", maxWidth: 180 }} noWrap>
                                {f.name}
                            </Typography>
                            <IconButton size="small" sx={{ p: 0.1 }}
                                onClick={() => setPendingFiles((prev) => prev.filter((_, k) => k !== i))}>
                                <CloseRoundedIcon sx={{ fontSize: 12, color: "#94a3b8" }} />
                            </IconButton>
                        </Box>
                    ))}
                </Stack>
            )}

            <input ref={fileRef} type="file" hidden multiple
                onChange={(e) => {
                    setPendingFiles((prev) => [...prev, ...Array.from(e.target.files || [])]);
                    e.target.value = "";
                }} />

            {/* 클립·비행기를 입력칸 안에 둔다 — 바깥에 두면 여러 줄로 늘어날 때 버튼만 따로 논다. */}
            <TextField
                inputRef={inputRef}
                size="small" fullWidth multiline maxRows={5}
                placeholder="댓글을 남기세요 (Enter 전송 · Shift+Enter 줄바꿈)"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKeyDown}
                inputProps={{ style: { fontSize: 12.5 } }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end" sx={{ alignSelf: "flex-end", mb: 0.4 }}>
                            <Tooltip title="파일 첨부">
                                <IconButton size="small" onClick={() => fileRef.current?.click()} sx={{ p: 0.4 }}>
                                    <AttachFileRoundedIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="전송">
                                <span>
                                    <IconButton size="small" onClick={submit}
                                        disabled={(!draft.trim() && pendingFiles.length === 0) || busy}
                                        sx={{ p: 0.4, color: (draft.trim() || pendingFiles.length) ? "#2563eb" : "#cbd5e1" }}>
                                        {busy ? <CircularProgress size={14} /> : <SendRoundedIcon sx={{ fontSize: 17 }} />}
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </InputAdornment>
                    ),
                }}
            />
        </Box>
    );
}

/** 오늘이면 시각만, 그 외엔 날짜까지 — 목록이 길어질수록 날짜가 반복되면 읽기 나쁘다. */
function formatWhen(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    if (sameDay) return `${hh}:${mm}`;
    return `${d.getMonth() + 1}.${d.getDate()} ${hh}:${mm}`;
}
